import React, { useState, useEffect, useRef } from 'react';
import { useUser } from 'util/UserContext';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from 'config/axiosConfig';
import { Comment } from 'util/constants';
import CommentInput from 'components/Comments/CommentInput';
import type { CommentInputContext } from 'components/Comments/CommentInput';
import RenderComments from 'components/Comments/RenderComments';
import AddButton from 'ui/AddButton';
import VerticalSeparator from 'ui/VerticalSeparator';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { AxiosError } from 'axios';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import { PrivacyLevel } from 'util/userUtils';
import { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import SpillTheTeaModal from 'components/Posts/SpillTheTeaModal';
import CreateTeabateArgument from 'components/Teabates/CreateTeabateArgument';
import { Issue, Impact } from 'util/constants';
import EmptyState from 'ui/EmptyState';
import type { PollDraft } from 'types/poll';
import { uploadEntityMediaDirect } from 'util/entityMediaUpload';
import { apiRoutes } from "util/apiRoutes";
import { useCommentsByCommentableQuery } from "hooks/queries/useCommentsQuery";
import { logger } from "lib/logger";
import { buildDraftStorageKey } from "util/draftStorage";
import { getTaskUiStrings, resolveTaskLocale } from "i18n/taskUi";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";

interface Props {
  commentable_type: Comment['commentable_type'];
  commentable_id: Comment['commentable_id'];
  isEntitySection: boolean;
}

const CommentsSection: React.FC<Props> = ({
  commentable_type,
  commentable_id,
  isEntitySection,
}) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const taskUi = getTaskUiStrings(resolveTaskLocale());
  const draftUi = getDraftUiStrings(resolveDraftLocale());
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const commentInputRef = useRef<HTMLDivElement>(null);
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const { commentId } = useParams<{ commentId: string }>();
  const [error, setError] = useState<string>('');
  const [isTeabateModalOpen, setIsTeabateModalOpen] = useState(false);
  const [issueForTeabate, setIssueForTeabate] = useState<Issue | null>(null);
  const [isTeabateArgumentModalOpen, setIsTeabateArgumentModalOpen] = useState(false);
  const [impactForArgument, setImpactForArgument] = useState<Impact | null>(null);
  const [argumentType] = useState<'agree' | 'disagree'>('agree');
  const [pendingNewCommentMedia, setPendingNewCommentMedia] = useState<FileList | null>(null);
  const newCommentDraftKey = currentUser
    ? buildDraftStorageKey("draft", "comment", "new", currentUser.id, commentable_type, commentable_id)
    : null;
  // Discussion contract: "every Discussion thread shows regular
  // comments AND teabate arguments." We express that intent
  // declaratively via the `kinds` allowlist on issue + teabate
  // surfaces. On the issue Discussion the backend interprets
  // `kinds=['comment','argument']` as a request to also inline
  // `kind='argument'` rows attached to this issue's child teabates
  // (via a `posts.issue_id` join). On the teabate's own Discussion
  // the same allowlist returns argument rows natively + any plain
  // comments left on the teabate. For every other commentable_type
  // we leave the allowlist undefined so the backend applies the
  // default Discussion behavior (hides task/principle/edge_note).
  const discussionKinds: ReadonlyArray<string> | undefined =
    commentable_type === 'issue' || commentable_type === 'teabate'
      ? ['comment', 'argument']
      : undefined;
  const commentsQuery = useCommentsByCommentableQuery(
    commentable_type,
    commentId ? null : commentable_id,
    discussionKinds ? { kinds: discussionKinds } : undefined
  );
  const isCommentsLoading = commentId ? loading : commentsQuery.isLoading || loading;

  const filterVisibleComments = (allComments: Comment[]) =>
    allComments.filter((comment: Comment) => {
      if (comment.parent_id === null) {
        return comment.is_active !== false;
      }
      return true;
    });

  useEffect(() => {
    // Single-thread route (`/.../comments/:commentId`) bypasses the
    // TanStack Query path and fetches just the replies for one node.
    // The full-thread path runs through `useCommentsByCommentableQuery`
    // above with `includeArguments` opt-in, so there's no dead
    // teabate-arguments branch to keep here.
    const fetchReplies = async (rootCommentId: string) => {
      try {
        const response = await api.get(apiRoutes.comments.replies(rootCommentId));
        const allComments = response.data as Comment[];
        setComments(filterVisibleComments(allComments));
        setLoading(false);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          setError('404: Failed to load comments. Please try again later.');
        } else {
          setError('Failed to load comments. Please try again later.');
        }
        setLoading(false);
      }
    };

    // Side-data fetches needed by the modals:
    //   - Issue object for the "Start a teabate" composer (issue surface).
    //   - Teabate post object for the "Add Argument" composer (teabate
    //     surface). The surface is the teabate post itself
    //     (`commentable_type='teabate'`, `posts.kind='teabate'`).
    const fetchEntityData = async () => {
      if (commentable_type === 'issue') {
        try {
          const response = await api.get(apiRoutes.issues.byId(commentable_id));
          setIssueForTeabate(response.data);
        } catch (error) {
          logger.error("Failed to fetch issue data for teabate", { error });
        }
      } else if (commentable_type === 'teabate') {
        try {
          const response = await api.get(apiRoutes.posts.byId(commentable_id));
          setImpactForArgument(response.data);
        } catch (error) {
          logger.error("Failed to fetch teabate data for argument", { error });
        }
      }
    };

    if (commentId) {
      fetchReplies(commentId);
    } else {
      setLoading(false);
    }
    fetchEntityData();
  }, [commentable_type, commentable_id, commentId]);

  useEffect(() => {
    if (!commentId && commentsQuery.data) {
      setComments(filterVisibleComments(commentsQuery.data));
    }
  }, [commentId, commentsQuery.data]);

  const updateCommentsRecursively = (commentsArray: Comment[], updateFunction: (comment: Comment) => Comment): Comment[] => {
    return commentsArray.map(comment => {
      const updatedComment = updateFunction(comment);
      if (updatedComment.replies && updatedComment.replies.length > 0) {
        return {
          ...updatedComment,
          replies: updateCommentsRecursively(updatedComment.replies, updateFunction)
        };
      }
      return updatedComment;
    });
  };

  const addNewComment = async (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    mediaFiles?: FileList | null,
    pollDraft?: PollDraft | null,
    context?: CommentInputContext | null,
    kind: "comment" | "principle" = "comment"
  ) => {
    try {
      // Reduce poll payload to backend-supported fields only
      const pollPayload = pollDraft
        ? {
            statement: pollDraft.statement,
            poll_type: pollDraft.poll_type,
            options: pollDraft.options.map((option) => ({
              option_text: option.option_text,
              option_entity_type: option.option_entity_type,
              option_entity_id: option.option_entity_id
            }))
          }
        : undefined;

      const response = await api.post(apiRoutes.comments.byCommentable(commentable_type, commentable_id), {
        content,
        kind,
        properties: {
          privacy: privacy
        },
        poll: pollPayload,
        place_id: context?.placeId ?? null,
        timeline_ts: context?.timelineTs ?? null,
      });
      let newComment = response.data;
      await api.post(apiRoutes.votes.comment(newComment.id), { value: 1 });

      // Add coauthors if any and not anonymous
      if (coauthors.length > 0 && privacy !== 'anonymous') {
          try {
              await api.post(apiRoutes.coauthors.request("comment", newComment.id), {
                  addedCoauthors: coauthors.map(c => ({
                      id: c.id,
                      type: c.type
                  }))
              });
          } catch (err) {
              logger.error("Error adding comment coauthors", { err });
              // Continue even if coauthors couldn't be added
          }
      }

      // Upload media if provided
      if (mediaFiles && mediaFiles.length > 0) {
        try {
          await uploadEntityMediaDirect("comment", newComment.id, Array.from(mediaFiles).map((file, index) => ({
            file,
            order: index + 1
          })));
        } catch {}
      }

      const updatedCommentResponse = await api.get(apiRoutes.comments.byId(newComment.id));
      newComment = {
        ...updatedCommentResponse.data,
        replies: [], // Initialize replies as an empty array
      };
      setComments((previousComments) => [newComment, ...previousComments]);
      toast.success(kind === "principle" ? taskUi.principleAdded : 'Comment added successfully');
    } catch (error) {
      logger.error("Error adding new comment", { error });
      toast.error(kind === "principle" ? taskUi.principleCreateError : 'Failed to add comment');
    }
  };

  const saveNewCommentDraft = async (
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    pollDraft?: PollDraft | null,
    context?: CommentInputContext | null
  ) => {
    const pollPayload = pollDraft
      ? {
          statement: pollDraft.statement,
          poll_type: pollDraft.poll_type,
          options: pollDraft.options.map((option) => ({
            option_text: option.option_text,
            option_entity_type: option.option_entity_type,
            option_entity_id: option.option_entity_id
          }))
        }
      : undefined;
    await api.post(apiRoutes.comments.byCommentable(commentable_type, commentable_id), {
      content,
      status: "draft",
      properties: {
        privacy,
        draft_coauthors: coauthors.map((coauthor) => ({ id: coauthor.id, type: coauthor.type })),
      },
      poll: pollPayload,
      place_id: context?.placeId ?? null,
      timeline_ts: context?.timelineTs ?? null,
    });
    toast.success(draftUi.draftSaved);
  };

  const addReply = async (
    parent_id: number,
    content: string,
    privacy: PrivacyLevel,
    coauthors: CoauthorSuggestion[],
    mediaFiles?: FileList | null,
    pollDraft?: PollDraft | null,
    context?: CommentInputContext | null
  ) => {
    try {
      // Special case for refresh trigger from rebuttals
      if (parent_id === -1 && content === "REFRESH_COMMENTS") {
        if (!commentId) {
          await commentsQuery.refetch();
          return;
        }
        const response = await api.get(apiRoutes.comments.byCommentable(commentable_type, commentable_id));
        const allComments = response.data as Comment[];
        setComments(filterVisibleComments(allComments));
        return;
      }
      
      const pollPayload = pollDraft
        ? {
            statement: pollDraft.statement,
            poll_type: pollDraft.poll_type,
            options: pollDraft.options.map((option) => ({
              option_text: option.option_text,
              option_entity_type: option.option_entity_type,
              option_entity_id: option.option_entity_id
            }))
          }
        : undefined;

      const response = await api.post(apiRoutes.comments.byCommentable(commentable_type, commentable_id), {
        content,
        parent_id: parent_id,
        properties: {
          privacy: privacy
        },
        poll: pollPayload,
        place_id: context?.placeId ?? null,
        timeline_ts: context?.timelineTs ?? null,
      });
      let newReply = response.data;
      await api.post(apiRoutes.votes.comment(newReply.id), { value: 1 });

      // Add coauthors if any and not anonymous
      if (coauthors.length > 0 && privacy !== 'anonymous') {
          try {
              await api.post(apiRoutes.coauthors.request("comment", newReply.id), {
                  addedCoauthors: coauthors.map(c => ({
                      id: c.id,
                      type: c.type
                  }))
              });
          } catch (err) {
              logger.error("Error adding reply coauthors", { err });
              // Continue even if coauthors couldn't be added
          }
      }

      if (mediaFiles && mediaFiles.length > 0) {
        try {
          await uploadEntityMediaDirect("comment", newReply.id, Array.from(mediaFiles).map((file, index) => ({
            file,
            order: index + 1
          })));
        } catch {}
      }

      const updatedReplyResponse = await api.get(apiRoutes.comments.byId(newReply.id));
      newReply = {
        ...updatedReplyResponse.data,
        replies: [], // Initialize replies as an empty array
      };

      setComments(prevComments => updateCommentsRecursively(prevComments, comment => {
        if (comment.id === parent_id) {
          return {
            ...comment,
            replies: [...(comment.replies || []), newReply]
          };
        }
        return comment;
      }));
      toast.success('Reply added successfully');
    } catch (error) {
      logger.error("Error adding reply", { error });
      toast.error('Failed to add reply');
    }
  };

  const updateComment = async (commentId: number, updatedContent: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[], mediaFiles?: FileList | null, editMediaState?: any) => {
    try {
      const response = await api.put(apiRoutes.comments.byId(commentId), {
        content: updatedContent,
        privacy: privacy
      });

      const updatedComment = response.data;

      if ((addedCoauthors.length > 0 || removedCoauthors.length > 0) && privacy !== 'anonymous') {
        try {
          await api.post(apiRoutes.coauthors.request("comment", commentId), {
            addedCoauthors: addedCoauthors.map((c: any) => ({
              id: c.id,
              type: c.type
            })),
            removedCoauthors: removedCoauthors.map((c: any) => ({
              id: c.id,
              type: c.type
            }))
          })
        } catch (err) {
          logger.error("Error updating comment coauthors", { err });
        }
      }

      // Handle media changes from edit state
      if (editMediaState) {
        // Delete removed media
        if (editMediaState.deletedKeys && editMediaState.deletedKeys.length > 0) {
          for (const key of editMediaState.deletedKeys) {
            try {
              await api.delete(apiRoutes.entities.mediaKey("comment", commentId, encodeURIComponent(key)));
            } catch (err) {
              logger.error("Error deleting comment media", { err });
            }
          }
        }

        // Reorder existing media if changed
        if (editMediaState.reorderMap && Object.keys(editMediaState.reorderMap).length > 0) {
          // Convert reorderMap to array of { key, order } objects as expected by backend
          const mediaOrders = Object.entries(editMediaState.reorderMap).map(([key, order]) => ({
            key: key,
            order: order
          }));
          
          try {
            await api.patch(apiRoutes.entities.mediaReorder("comment", commentId), {
              mediaOrders: mediaOrders
            });
          } catch (err) {
            logger.error("Error reordering comment media", { err });
          }
        }
      }

      // Upload new media if provided
      if (mediaFiles && mediaFiles.length > 0) {
        try {
          await uploadEntityMediaDirect("comment", commentId, Array.from(mediaFiles).map((file, index) => ({
            file,
            order: index + 1
          })));
        } catch {}
      }

      setComments(prevComments => updateCommentsRecursively(prevComments, comment => {
        if (comment.id === commentId) {
          return { ...comment, ...updatedComment };
        }
        return comment;
      }));
      toast.success('Comment updated successfully');
    } catch (error) {
      logger.error("Error updating comment", { error });
      toast.error('Failed to update comment');
    }
  };

  const deleteComment = async (commentId: number) => {
    try {
      await api.delete(apiRoutes.comments.byId(commentId));

      setComments(prevComments => updateCommentsRecursively(prevComments, comment => {
        if (comment.id === commentId) {
          // Replace the comment content with a placeholder
          return { ...comment, content: '[Deleted by user]', username: '[deleted]' };
        }
        return comment;
      }));
      toast.success('Comment deleted successfully');
    } catch (error) {
      logger.error("Error deleting comment", { error });
      toast.error('Failed to delete comment');
    }
  };

  const handleOpenArgumentModal = () => {
    setIsTeabateArgumentModalOpen(true);
  };

  const handleArgumentSubmitSuccess = () => {
    if (!commentId) {
      void commentsQuery.refetch();
      return;
    }
    // Fallback for direct comment-thread routes.
    const fetchComments = async () => {
      try {
        const response = await api.get(apiRoutes.comments.byCommentable(commentable_type, commentable_id));
        const allComments = response.data as Comment[];
        setComments(filterVisibleComments(allComments));
      } catch (error) {
        logger.error("Error refreshing comments after argument", { error });
      }
    };
    void fetchComments();
  };

  /*useEffect(() => {
    if (showFullInput && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [showFullInput]);*/

  if (error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find the comments for this ${commentable_type}.`} errorSubtext={`Double check the URL or try reloading the page.`} buttonText={`View Other Comments`} buttonHref={`.`} /> : <ServerErrorPage />
  }

  if (commentId && comments.length < 1) {
    return <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find this comment.`} errorSubtext={`It may be only visible to specific users. Double check the URL or try reloading the page.`} buttonText={`View Other Comments`} buttonHref={`.`} />
  }

  if (commentId) {
    return (
      <div className='comment-id-section pb-6 overflow-x-hidden'>
        <div className="flex justify-between items-center gap-2 mb-2 mx-2">
          {comments[0]?.root_id ? (
            <Link to={`../${comments[0]?.root_id}`} relative="path" preventScrollReset={true} className="internal-link text-sm font-semibold px-2">See full thread</Link>
          ) : (
            <span className="text-sm font-normal">This is the root comment</span>
          )}
          <span className="flex-1 h-px mx-xs bg-gray-300 dark:bg-eoe-dark-border-main" data-id="line"></span>
          <Link to=".." relative="path" preventScrollReset={true} className="internal-link text-sm font-semibold px-2">See all comments</Link>
        </div>
        <RenderComments
          comments={comments}
          maxDepth={30}
          isAllCollapsed={false}
          onAddReply={addReply}
          onUpdateComment={updateComment}
          onDeleteComment={deleteComment}
          commentable_type={commentable_type}
          commentable_id={commentable_id}
          isEntitySection={true}
          showEntityCompactCard={false}
        />
      </div>
    );
  }
  
  return (
    <div className='comments-section pb-6 overflow-x-hidden'>
      <div ref={commentSectionRef}>
        {!currentUser ? (
          <>
            <div className='flex mb-1'>
              <AddButton
                  tooltipTitle="Start a thread"
                  buttonText="Comment"
                  onClick={() => setShowAuthModal(true)}
                  className="mx-2 my-2 w-full"
              />
            </div>
          </>
        ) : ( 
          <div className="my-2 mx-4">
            <CommentInput
              action="add"
              onSubmit={async (content, privacy, coauthors, _addedCoauthors, _removedCoauthors, pollDraft, context) => {
                await addNewComment(content, privacy, coauthors, pendingNewCommentMedia, pollDraft, context, "comment");
                setPendingNewCommentMedia(null);
              }}
              onSaveDraft={saveNewCommentDraft}
              buttonText="Comment"
              ref={commentInputRef}
              onMediaSelected={(files) => setPendingNewCommentMedia(files)}
              draftKey={newCommentDraftKey}
              draftEnabled={Boolean(newCommentDraftKey)}
              showTimelinePlace={true}
              isMobile={userIsMobile}
            />
            {commentable_type === 'issue' && (
              <div className="mt-2">
                <VerticalSeparator text="OR" />
                <div className="mt-2">
                <AddButton
                  tooltipTitle="Start a teabate"
                  buttonText="Teabate"
                  onClick={() => setIsTeabateModalOpen(true)}
                  className="w-full"
                />
                </div>
              </div>
            )}
            {commentable_type === 'teabate' && impactForArgument && (
              <div className="mt-2">
                <VerticalSeparator text="OR" />
                <div className="mt-2">
                  <AddButton
                    tooltipTitle="Add an argument"
                    buttonText="Argument"
                    onClick={handleOpenArgumentModal}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
        {isCommentsLoading && (
            <LoadingSkeleton
                avatarSize='h-6 w-6'
                variant='comment-card'
                count={6}
                containerClassName='mx-2 mt-0 pr-4'
            />
        )}
        {!isCommentsLoading &&comments.length === 0 ? (
          <EmptyState
            title="No comments yet" 
            subtitle="Be the first to start a discussion!"
            className="m-4"
          />
        ) : (
        <>
          <RenderComments
            comments={comments}
            maxDepth={30}
            isAllCollapsed={true}
            onAddReply={addReply}
            onUpdateComment={updateComment}
            onDeleteComment={deleteComment}
            commentable_type={commentable_type}
            commentable_id={commentable_id}
            isEntitySection={true}
            showEntityCompactCard={false}
          />
        </>
      )}
      {isTeabateModalOpen && issueForTeabate && (
        <SpillTheTeaModal
          open={isTeabateModalOpen}
          onClose={() => setIsTeabateModalOpen(false)}
          onImpactAdded={() => {}}
          initialSelectedIssue={issueForTeabate}
          customTitle="Start a teabate"
          fixedPostType="teabate"
          hidePostTypeButtons={true}
        />
      )}
      {isTeabateArgumentModalOpen && impactForArgument && (
        <CreateTeabateArgument
          isOpen={isTeabateArgumentModalOpen}
          onClose={() => setIsTeabateArgumentModalOpen(false)}
          teabate={impactForArgument}
          argumentType={argumentType}
          onSubmitSuccess={handleArgumentSubmitSuccess}
          onReopenModal={() => setIsTeabateArgumentModalOpen(true)}
        />
      )}
    </div>
  );
};

export default CommentsSection;